import _ from 'underscore';
import Backbone from 'backbone';
import { toQueryString } from 'utils/UrlUtil';

var Events = {
    ON_MORE_MEMBERS_FETCHED: 'ON_MORE_MEMBERS_FETCHED'
};

var PagedCollection = Backbone.Collection.extend({
    baseUrl: null, // provide this in the implementation to provide the base url for the search

    url: function() {
        let url = this.getBaseUrl();
        const queryObject = {};
        if (this.verb !== 'POST') {
            if (this.keyword) {
                queryObject['keyword'] = this.keyword;
            }

            if (typeof this.startIndex === 'number') {
                queryObject['start'] = this.startIndex;
            }

            if (typeof this.limit === 'number') {
                queryObject['limit'] = this.limit;
            }

            if (typeof this.displayMutedStatus === 'boolean') {
                queryObject['displayMutedStatus'] = this.displayMutedStatus;
            }
        }

        return `${url}${toQueryString(queryObject)}`;
    },

    getBaseUrl: function() {
        if (_.isFunction(this.baseUrl)) {
            return this.baseUrl();
        } else {
            return this.baseUrl;
        }
    },

    /**
     * Sets up base members.
     */
    initializePagedCollection: function(args) {
        this.varHasMoreMembers = true; // Keeps track of whether there are more members to fetch.
        this.sizeBeforeLastFetch = 0; // The size of the collection prior to the last fetch
        this.totalLength = 0; // Since the append mode can be switched off, we need a way to store the total size of the displayed models
        this.appendMode = true; // When set to true, the returned collection will always contain all models (Every model from every page)
        // When set to false, each fetch will replace the collections models, always returning the <<limit>> number of models (until last page)

        if (args) {
            this.keyword = args.keyword; // The keyword to search for. May not be required for some services
            this.startIndex = args.startIndex; // The current start index. Incremented each time fetchNext() is called.
            this.limit = args.limit; // The size of the fetches.
            this.shouldSort = args.sort; // When set to false, prevents the added models to be sorted through the collection comparator function. Defaults to true.
            this.totalLengthUpperBound = args.totalLengthUpperBound;
        }

        if (args.hasOwnProperty('appendMode')) {
            this.appendMode = args.appendMode;
        }

        if (typeof this.startIndex === 'undefined') {
            this.startIndex = 1;
        }

        this.initialStartIndex = this.startIndex;
        this.verb = args.verb; // Can specify if this is a POST or a GET

        if (!this.verb) {
            this.verb = 'POST';
        }

        if (!_.isBoolean(this.shouldSort)) {
            this.shouldSort = true; // default
        }
    },

    /**
     * Simply calls fetch. The starting position and limit are managed internally. This function is provided mostly
     * to provide clarity for the API.
     */
    fetchNext: function(fetchOptions) {
        fetchOptions = fetchOptions || {};

        var successCallback = null;
        if (fetchOptions.success) {
            successCallback = fetchOptions.success;
        }

        if (this.appendMode) {
            fetchOptions.add = true;
            fetchOptions.remove = false;
        }

        fetchOptions.type = this.verb;
        fetchOptions.sort = this.shouldSort;

        if (this.verb === 'POST') {
            var data = {};
            data.start = this.startIndex;
            data.limit = this.limit;
            data.keyword = this.keyword;
            fetchOptions.data = data;
            fetchOptions.contentType = 'application/x-www-form-urlencoded; charset=UTF-8';
        }

        var _this = this;
        fetchOptions.success = function(collection, response, options) {
            _this.onFetchSuccess(collection, response, options);

            if (successCallback) {
                successCallback.call(_this, collection, response, options);
            }
        };

        if (this.hasMoreMembers()) {
            Backbone.Collection.prototype.fetch.call(this, fetchOptions);
        }
    },

    /**
     * Overloaded version of fetch which calls the FetchNext instead. FetchNext will then call the base version
     * of fetch. This prevents callers from circumventing the housekeeping in the FetchNext() function.
     */

    fetch: function(options) {
        this.fetchNext(options);
    },

    /**
     * Handler called when a fetch has been completed.
     */
    onFetchSuccess: function(collection, response, options) {
        if (this.appendMode) {
            this.startIndex = this.length + this.initialStartIndex;
            this.totalLength = this.length;
        } else {
            this.startIndex += this.length;
            this.totalLength += this.length;
        }

        this.checkIfHasMoreMembers();

        this.sizeBeforeLastFetch = this.totalLength;
        this.trigger(Events.ON_MORE_MEMBERS_FETCHED);
    },

    checkIfHasMoreMembers: function() {
        // I know this could be consolidated, I left it expanded for clarity
        if (_.isUndefined(this.limit)) {
            // If no limit then there can't be any more members
            this.varHasMoreMembers = false;

            // If the fetch count is less than request limit, this would happen on the first fetch, no more members need to be requested
            // If the total number of members after a fetch equals the the number of members before the fetch, nothing new was added and thus no more members need to be requested
        } else if (
            (typeof this.totalLengthUpperBound !== 'undefined' && this.totalLengthUpperBound === this.totalLength) ||
            this.totalLength < this.limit ||
            this.totalLength === this.sizeBeforeLastFetch ||
            this.totalLength % this.limit !== 0
        ) {
            this.varHasMoreMembers = false;
        }
    },

    /**
     * Returns whether or not there are more members to fetch. This is a little fuzzy as it makes some assumptions
     * based on the current count.
     */
    hasMoreMembers: function() {
        return this.varHasMoreMembers;
    }
});

PagedCollection.Events = Events;

export default PagedCollection;
